import isArray from 'lodash/isArray';
import noop from 'lodash/noop';

import { AnalyticsService } from './analytics.service';
import { sanitizeData } from './analytics-data-access.util';

export interface Rule {
  data?: {
    [inputKey: string]: any;
  };
  track?: {
    [outputKey: string]: any;
  };
}

export interface RuleSet {
  /**
   * @todo: how do we do this without an index signature?
   * index signature makes it so any rule can be set
   * we basically want [rulename in keyof this] but that doesn't seem to be a thing
   */
  [ruleName: string]: Rule;
}

export type AnalyticsTransforms<Rules extends RuleSet> = {
  [ruleName in keyof Rules]?: (
    d: Rules[ruleName]['data'],
  ) => Rules[ruleName]['track'];
};

interface AnalyticsTransformOptions {
  replaceArrays?: boolean;
}

export type AnalyticsTransformOptionsSet<Rules extends RuleSet> = {
  [ruleName in keyof Rules]?: AnalyticsTransformOptions;
};

export enum LinkType {
  DOWNLOAD = 'd',
  EXIT = 'e',
  OTHER = 'o',
}

export abstract class FeatureAnalyticsService<Rules extends RuleSet> {
  protected basePath?: string;

  protected abstract get transforms(): AnalyticsTransforms<Rules>;
  protected transformOptions: AnalyticsTransformOptionsSet<Rules> = {};

  private static replaceArraysCustomizer(currentValue: any, newValue: any) {
    if (isArray(currentValue)) {
      return newValue;
    }
  }

  constructor(protected analytics: AnalyticsService) {}

  public track<R extends keyof Rules>(
    ruleName: R,
    data: Rules[R]['data'],
    dontSanitize: boolean = false,
  ) {
    this.analytics.track(
      ruleName.toString(),
      this.transformData(ruleName, data, dontSanitize),
      this.basePath,
      this.getCustomizer(ruleName),
    );
  }

  public trackEvent<R extends keyof Rules>(
    ruleName: R,
    data: Rules[R]['data'],
    linkType: LinkType = LinkType.OTHER,
    dontSanitize: boolean = false,
  ) {
    this.analytics.trackEvent(
      this.transformData(ruleName, data, dontSanitize) as object,
      linkType,
    );
  }

  public trackPage<R extends keyof Rules>(
    ruleName: R,
    data: Rules[R]['data'],
    dontSanitize: boolean = false,
  ) {
    this.analytics.trackPage(
      this.transformData(ruleName, data, dontSanitize) as object,
    );
  }

  public updateAdobeStore<R extends keyof Rules>(data: Rules[R]['data']): void {
    this.analytics.updateAdobeStore(data);
  }

  public updateStore<R extends keyof Rules>(
    ruleName: R,
    data: Rules[R]['data'],
    dontSanitize: boolean = false,
  ): void {
    this.analytics.updateStore(
      this.transformData(ruleName, data, dontSanitize),
      this.basePath,
      this.getCustomizer(ruleName),
    );
  }

  private getTransform<R extends keyof Rules>(
    ruleName: R,
  ): ((d: Rules[R]['data']) => Rules[R]['track']) | undefined {
    return this.transforms[ruleName];
  }

  private getCustomizer<R extends keyof Rules>(
    ruleName: R,
  ): (obj: any, src: any) => any {
    const options: AnalyticsTransformOptions | undefined =
      this.transformOptions[ruleName];
    if (options && options.replaceArrays) {
      return FeatureAnalyticsService.replaceArraysCustomizer;
    }

    return noop;
  }

  private transformData<R extends keyof Rules>(
    rule: R,
    data: Rules[R]['data'],
    dontSanitize: boolean,
  ): Rules[R]['track'] {
    if (!data) {
      return undefined;
    }

    const ruleTransform = this.getTransform(rule);

    if (!ruleTransform) {
      // eslint-disable-next-line no-console
      console.warn('update store called but no rule defined', {
        rule,
        data,
      });
      return;
    }

    if (dontSanitize) {
      return ruleTransform(data);
    }

    return sanitizeData(ruleTransform(data));
  }
}
